<template>
  <div>
    <!-- First-layer banner: shown until user makes an explicit decision. -->
    <div
      v-if="showBanner"
      class="cookies fixed-bottom"
    >
      <b-card class="cookies__card">
        <div class="cookies__layout">
          <div class="cookies__text">
            {{ $t('text.cookie-card') }} 🍪
            <a href="/privacy#cookies">{{ $t('basic.learn-more') }}</a>
          </div>
          <div class="cookies__actions">
            <b-button
              variant="outline-secondary"
              size="sm"
              pill
              class="cookies__btn"
              @click="openCustomize"
            >
              {{ $t('basic.customize-cookies') }}
            </b-button>
            <b-button
              variant="outline-secondary"
              size="sm"
              pill
              class="cookies__btn"
              @click="onRejectAll"
            >
              {{ $t('basic.reject-cookies') }}
            </b-button>
            <b-button
              variant="primary"
              size="sm"
              pill
              class="cookies__btn"
              @click="onAcceptAll"
            >
              {{ $t('basic.accept-cookies') }}
            </b-button>
          </div>
        </div>
      </b-card>
    </div>

    <!-- Second layer: granular preferences modal (Play system). -->
    <PlayModal
      v-model="showModal"
      :title="$t('text.cookie-card-title')"
      size="md"
      scrollable
    >
      <p class="cookie-modal__intro">
        {{ $t('text.cookie-modal-text') }}
        <a href="/privacy#cookies">{{ $t('basic.learn-more') }}</a>
      </p>

      <div class="cookie-modal__options">
        <PlayCheckbox
          :checked="true"
          disabled
        >
          {{ $t('text.cookies-1') }}
        </PlayCheckbox>
        <PlayCheckbox v-model="prefs.functional">
          {{ $t('text.cookies-2') }}
        </PlayCheckbox>
        <PlayCheckbox v-model="prefs.analytics">
          {{ $t('text.cookies-3') }}
        </PlayCheckbox>
      </div>

      <template #footer>
        <div class="cookie-modal__actions">
          <PlayButton
            variant="ghost"
            size="sm"
            @click="onRejectAll"
          >
            {{ $t('basic.reject-cookies') }}
          </PlayButton>
          <PlayButton
            variant="secondary"
            size="sm"
            @click="onSavePreferences"
          >
            {{ $t('basic.save-preferences') }}
          </PlayButton>
          <PlayButton
            variant="primary"
            size="sm"
            @click="onAcceptAll"
          >
            {{ $t('basic.accept-cookies') }}
          </PlayButton>
        </div>
      </template>
    </PlayModal>
  </div>
</template>

<script>
import { BCard, BButton } from 'bootstrap-vue'
import PlayModal from '@/components/play/PlayModal.vue'
import PlayCheckbox from '@/components/play/PlayCheckbox.vue'
import PlayButton from '@/components/play/PlayButton.vue'
import {
  getConsent,
  hasConsentDecision,
  acceptAll,
  rejectAll,
  setConsent,
  onOpenSettings,
} from '@/libs/consent'

export default {
  name: 'CookieWindow',
  components: {
    BCard,
    BButton,
    PlayModal,
    PlayCheckbox,
    PlayButton,
  },
  data() {
    return {
      // Banner is shown only when there is no stored decision yet.
      showBanner: false,
      // Modal is shown when user customises or explicitly re-opens settings.
      showModal: false,
      // `necessary` is always-on (rendered as a disabled, pre-checked box) so it
      // isn't tracked here — only the toggleable categories are.
      prefs: {
        functional: false,
        analytics: false,
      },
    }
  },
  mounted() {
    this.syncFromStorage()
    this.showBanner = !hasConsentDecision()
    // Allow other parts of the app (e.g. footer "Manage cookies" link) to
    // re-open the preferences modal at any time.
    this.unsubscribeOpen = onOpenSettings(() => {
      this.syncFromStorage()
      this.showModal = true
    })
  },
  beforeDestroy() {
    if (this.unsubscribeOpen) this.unsubscribeOpen()
  },
  methods: {
    syncFromStorage() {
      const c = getConsent()
      if (c) {
        this.prefs.functional = !!c.functional
        this.prefs.analytics = !!c.analytics
      }
    },
    openCustomize() {
      this.showModal = true
    },
    onAcceptAll() {
      acceptAll()
      this.prefs.functional = true
      this.prefs.analytics = true
      this.dismiss()
    },
    onRejectAll() {
      rejectAll()
      this.prefs.functional = false
      this.prefs.analytics = false
      this.dismiss()
    },
    onSavePreferences() {
      setConsent({
        functional: this.prefs.functional,
        analytics: this.prefs.analytics,
      })
      this.dismiss()
    },
    dismiss() {
      this.showBanner = false
      this.showModal = false
    },
  },
}
</script>

<style scoped>
/* ── Preferences modal (PlayModal body/footer slots) ── */
.cookie-modal__intro {
  margin: 0 0 1.25rem;
}

.cookie-modal__intro a {
  color: var(--c-secondary-strong);
  font-weight: 600;
  text-decoration: underline;
}

.cookie-modal__options {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

/* Footer slot fills the row and wraps the three actions on narrow screens. */
.cookie-modal__actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  width: 100%;
}

.cookies {
  /* Stay above the floating demo Widget (z-index: 1035) but below Bootstrap modals (1050+ are still on top via portal). */
  z-index: 1040;
  padding: 12px;
  pointer-events: none; /* allow clicks to pass around the centered card on wide screens */
}

.cookies__card {
  max-width: 880px;
  margin: 0 auto;
  pointer-events: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
}

.cookies__layout {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.cookies__text {
  font-size: 0.875rem;
  line-height: 1.5;
}

.cookies__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.cookies__btn {
  width: 100%;
  /* Mobile tap target — iOS HIG recommends 44px min, Material 48dp.
     Bootstrap's `size="sm"` collapses buttons to ~31px which is too tight on
     a small viewport, especially for a consent UI where misclicks have legal
     consequences. */
  min-height: 44px;
  padding-top: 0.625rem;
  padding-bottom: 0.625rem;
  font-size: 0.9375rem;
}

/* Side-by-side layout from md (≥768px). */
@media (min-width: 768px) {
  .cookies__layout {
    flex-direction: row;
    align-items: center;
    gap: 24px;
  }
  .cookies__text {
    flex: 1 1 auto;
    text-align: left;
  }
  .cookies__actions {
    flex: 0 0 auto;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 8px;
  }
  .cookies__btn {
    width: auto;
    /* Desktop reverts to compact sm sizing — pointer accuracy is high, no
       tap-target requirement, and the row needs to stay visually balanced
       with the body text. */
    min-height: 0;
    padding-top: 0.25rem;
    padding-bottom: 0.25rem;
    font-size: 0.875rem;
  }
}
</style>
